import React, {useState, useEffect, useRef} from 'react'
import {FieldHookConfig, useField, useFormikContext} from 'formik'

function fieldErrorMessage(error: unknown): string | undefined {
  if (error == null || error === false) return undefined
  if (typeof error === 'string') return error
  if (Array.isArray(error)) {
    const parts = error
      .map((e) => fieldErrorMessage(e))
      .filter((s): s is string => Boolean(s))
    return parts.length ? parts.join(', ') : undefined
  }
  if (typeof error === 'object') {
    for (const v of Object.values(error as Record<string, unknown>)) {
      const m = fieldErrorMessage(v)
      if (m) return m
    }
  }
  return undefined
}

type TextInputProps = FieldHookConfig<string> & {
  label?: string
  labelPosition?: 'top' | 'left'
  type?: string
  placeholder?: string
  className?: string
  wrapperClassName?: string
  currency?: boolean
  addon?: React.ReactNode
  readOnly?: boolean
  onValueChange?: (value: string | null) => void
}

// 🔹 TR formatındaki string'i sayıya çevir
const parseTurkishNumber = (value: string): number | null => {
  if (!value) return null
  const cleaned = value.replace(/\./g, '').replace(',', '.')
  const num = parseFloat(cleaned)
  return isNaN(num) ? null : num
}

// 🔹 Sayıyı TR formatında string olarak döndür
const formatCurrency = (value: string | number | null | undefined): string => {
  if (value == null || value === '') return ''
  const num = typeof value === 'number' ? value : parseFloat(value)
  if (isNaN(num)) return ''
  return num.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const TextInput: React.FC<TextInputProps> = ({
  label,
  labelPosition = 'top',
  type = 'text',
  className = '',
  placeholder = '',
  wrapperClassName = '',
  currency = false,
  addon,
  readOnly = false,
  onValueChange,
  ...props
}) => {
  const [field, meta, helpers] = useField(props)
  const {submitCount} = useFormikContext()
  const errMsg = fieldErrorMessage(meta.error)
  const showInvalid = (meta.touched || submitCount > 0) && errMsg

  const inputRef = useRef<HTMLInputElement>(null)
  const [displayValue, setDisplayValue] = useState<string>('')

  useEffect(() => {
    if (currency) {
      const v = field.value
      setDisplayValue(v != null && v !== '' ? formatCurrency(String(v)) : '')
      return
    }
    const v = field.value as unknown
    if (v === undefined || v === null || v === '') {
      setDisplayValue('')
    } else {
      // 0 ve sayısal Formik değerleri için || '' kullanma (0 → '' hatası)
      setDisplayValue(String(v))
    }
  }, [field.value, currency])

  useEffect(() => {
    if (field.value == null || field.value === '') {
      if (currency) {
        helpers.setValue('0.00')
      }
    }
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target
    const raw = input.value
    if (currency) {
      const prev = displayValue
      const caret = input.selectionStart ?? raw.length

      // 🔹 Filtrele
      let cleaned = raw.replace(/[^\d.,]/g, '')

      // 🔹 En fazla 2 ondalık
      const parts = cleaned.split(',')
      if (parts.length > 1 && parts[1].length > 2) {
        parts[1] = parts[1].slice(0, 2)
        cleaned = parts.join(',')
      }

      // 🔹 Sayısal değer
      const parsed = parseTurkishNumber(cleaned)
      const normalized = parsed != null ? parsed.toFixed(2) : ''

      // 🔹 Görseli güncelle
      setDisplayValue(cleaned)
      helpers.setValue(normalized)
      onValueChange?.(normalized || null)

      // 🔹 Caret pozisyonunu koru (asenkron)
      requestAnimationFrame(() => {
        if (inputRef.current && inputRef.current.type === 'text') {
          const diff = cleaned.length - prev.length
          let newPos = Math.max(caret, 0)
          if (caret === 4) newPos = Math.max(caret + diff, 0)
          inputRef.current.setSelectionRange(newPos, newPos)
        }
      })
    } else {
      setDisplayValue(raw)
      helpers.setValue(raw)
      onValueChange?.(raw || null)
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    field.onBlur(e)
    if (currency) {
      const parsed = parseTurkishNumber(displayValue)
      const formatted = parsed != null ? formatCurrency(parsed) : ''
      setDisplayValue(formatted)
    }
  }

  const renderLabel = () => {
    if (!label) return null

    return (
      <label className={`form-label fw-bold ${labelPosition === 'left' ? 'mb-0' : ''}`}>
        {label}
      </label>
    )
  }

  const inputClassName = ['form-control', showInvalid && 'is-invalid', className]
    .filter(Boolean)
    .join(' ')

  const inputModeProp: React.HTMLAttributes<HTMLInputElement>['inputMode'] = currency
    ? 'decimal'
    : type === 'number'
      ? 'numeric'
      : undefined

  const inputElement = (
    <div className={labelPosition === 'left' && label ? 'flex-grow-1' : 'w-100'}>
      {addon ? (
        <div>
          <div className='input-group'>
            <input
              ref={inputRef}
              type={type}
              {...(inputModeProp ? {inputMode: inputModeProp} : {})}
              placeholder={placeholder}
              className={inputClassName}
              value={displayValue}
              onChange={handleChange}
              onBlur={handleBlur}
              name={field.name}
              disabled={readOnly}
            />
            {addon}
          </div>
          {showInvalid && (
            <div className='invalid-feedback d-block w-100' style={{display: 'block'}}>
              {errMsg}
            </div>
          )}
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type={type}
            {...(inputModeProp ? {inputMode: inputModeProp} : {})}
            placeholder={placeholder}
            className={inputClassName}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            name={field.name}
            disabled={readOnly}
          />
          {showInvalid && (
            <div className='invalid-feedback d-block w-100' style={{display: 'block'}}>
              {errMsg}
            </div>
          )}
        </>
      )}
    </div>
  )

  if (labelPosition === 'left' && label) {
    return (
      <div className={`${wrapperClassName} h-100`}>
        <div className='d-flex align-items-center gap-3 h-100'>
          <div className='flex-shrink-0'>{renderLabel()}</div>
          {inputElement}
        </div>
      </div>
    )
  }

  return (
    <div className={`${wrapperClassName} h-100`}>
      {renderLabel()}
      {inputElement}
    </div>
  )
}

export default TextInput
